import type { CaseVersion } from "@/lib/api";

/**
 * Resolve version IDs for selected locales.
 * When preferredVersion's locale is selected and has phone JSON, use that
 * exact version. Otherwise pick highest version_n with phone (mirrors server
 * pickLocalePublishVersion).
 */
export function resolvePublishVersionIds(
  selectedLocales: string[],
  versions: CaseVersion[],
  preferredVersion?: CaseVersion | null,
): string[] {
  const ids: string[] = [];
  for (const locale of selectedLocales) {
    if (
      preferredVersion &&
      preferredVersion.locale === locale &&
      preferredVersion.phoneJsonR2Key
    ) {
      ids.push(preferredVersion.id);
      continue;
    }
    let best: CaseVersion | null = null;
    for (const v of versions) {
      if (v.locale !== locale || !v.phoneJsonR2Key) continue;
      if (!best) {
        best = v;
        continue;
      }
      if (v.versionN > best.versionN) {
        best = v;
        continue;
      }
      if (v.versionN < best.versionN) continue;
      if (v.status === "published" && best.status !== "published") best = v;
    }
    if (best) ids.push(best.id);
  }
  return ids;
}

/**
 * Label for the versions-table production column.
 * null = full release (show 全量). Targeted publish returns group names.
 */
export function productionAudienceLabel(
  groupIds: string[] | undefined,
  groups: Array<{ id: string; name: string }>,
): string | null {
  if (!groupIds || groupIds.length === 0) return null;
  const byId = new Map(groups.map((g) => [g.id, g.name]));
  const names: string[] = [];
  for (const id of groupIds) {
    const name = byId.get(id)?.trim();
    if (name) names.push(name);
  }
  if (names.length === 0) return "定向";
  return names.join(" · ");
}

/** Case-list production badge. null when the case is not published. */
export function caseProductionAudienceLabel(
  audience:
    | {
        kind: "full" | "targeted" | "mixed";
        groupIds: string[];
        publishGroups: Array<{ id: string; name: string }>;
      }
    | null
    | undefined,
): string | null {
  if (!audience) return null;
  if (audience.kind === "full") return "全量";
  if (audience.kind === "mixed") return "混合";
  return productionAudienceLabel(audience.groupIds, audience.publishGroups) ?? "定向";
}

export function localePublishBadgeVariant(
  item: {
    published: boolean;
    kind: "unpublished" | "full" | "targeted";
  },
): "outline" | "success" | "warning" {
  if (!item.published || item.kind === "unpublished") return "outline";
  if (item.kind === "targeted") return "warning";
  return "success";
}

export function localePublishTooltip(item: {
  published: boolean;
  kind: "unpublished" | "full" | "targeted";
  groupIds: string[];
  publishGroups: Array<{ id: string; name: string }>;
}): string {
  if (!item.published || item.kind === "unpublished") return "未发布";
  if (item.kind === "full") return "全量发布";
  const groups = productionAudienceLabel(item.groupIds, item.publishGroups);
  if (!groups || groups === "定向") return "定向发布";
  return `定向发布：${groups}`;
}

/** Locales that currently have a live published pointer. */
export function publishedLocalesFromStatus(
  locales: Array<{ locale: string; publishedVersionId: string | null }>,
): Array<{ locale: string; versionId: string }> {
  return locales
    .filter(
      (loc): loc is { locale: string; publishedVersionId: string } =>
        Boolean(loc.publishedVersionId),
    )
    .map((loc) => ({
      locale: loc.locale,
      versionId: loc.publishedVersionId,
    }))
    .sort((a, b) => a.locale.localeCompare(b.locale));
}
